import { CardContent, Separator } from '@ais-ui/ui-core';
import { EnvironmentalTargetInfo } from '@ais-ui/websockets';
import React from 'react';
import { useSettings } from '../../Settings/SettingsProvider';

interface EnvironmentalInfoTooltipProps {
  environmentalInfo: EnvironmentalTargetInfo;
}

interface EnvironmentalInfoTooltipItemProps {
  label: string;
  value: string;
}

const EnvironmentalInfoTooltipItem = ({
  label,
  value,
}: EnvironmentalInfoTooltipItemProps) => {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-4 max-w-[12rem]">
      <p className="text-xs font-medium flex-none text-left max-w-[6rem]">
        {label}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 flex-grow text-right">
        {value ? value : '-'}
      </p>
    </div>
  );
};

export const EnvironmentalInfoTooltip = ({
  environmentalInfo,
}: EnvironmentalInfoTooltipProps) => {
  const { settings } = useSettings();
  const ribbonSettings = settings['ribbonPlugin.settings'];

  // Check if all settings are falsy or corresponding data is missing
  const hasAirRelatedData =
    ribbonSettings?.show_methyd_air_related &&
    (environmentalInfo.windReport?.windGust ||
      environmentalInfo.windReport?.windDirection ||
      environmentalInfo.windReport?.windGustDirection ||
      environmentalInfo.windReport?.avgWindSpeed ||
      environmentalInfo.weatherReport?.airTemperature ||
      environmentalInfo.weatherReport?.airPressure ||
      environmentalInfo.weatherReport?.airPressureTrend ||
      environmentalInfo.weatherReport?.dewPoint);

  const hasWaterRelatedData =
    ribbonSettings?.show_methyd_water_related &&
    (environmentalInfo.seaStateReport?.seaState ||
      environmentalInfo.seaStateReport?.swellDirection ||
      environmentalInfo.seaStateReport?.swellHeight ||
      environmentalInfo.seaStateReport?.swellPeriod ||
      environmentalInfo.seaStateReport?.signWaveHeight ||
      environmentalInfo.seaStateReport?.waterTemperature ||
      environmentalInfo.seaStateReport?.waterTemperatureDepth ||
      environmentalInfo.seaStateReport?.waveDirection ||
      environmentalInfo.seaStateReport?.wavePeriod);

  const hasMetadataInfo =
    (ribbonSettings?.show_methyd_station_name &&
      environmentalInfo.stationName) ||
    (ribbonSettings?.show_methyd_site_id && environmentalInfo.siteId) ||
    (ribbonSettings?.show_methyd_owner && environmentalInfo.owner) ||
    (ribbonSettings?.show_methyd_data_timeout &&
      environmentalInfo.dataTimeout) ||
    (ribbonSettings?.show_methyd_mmsi && environmentalInfo.mmsi) ||
    (ribbonSettings?.show_methyd_latitude && environmentalInfo.latitude) ||
    (ribbonSettings?.show_methyd_longitude && environmentalInfo.longitude) ||
    (ribbonSettings?.show_methyd_last_origin && environmentalInfo.lastOrigin);

  // Return null if no items will be shown
  if (!hasAirRelatedData && !hasWaterRelatedData && !hasMetadataInfo) {
    return null;
  }

  return (
    <CardContent className="pt-0 pb-2 -mt-1 space-y-2 px-3">
      <Separator className="-ml-1"></Separator>
      <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
        {ribbonSettings?.show_methyd_air_related &&
          environmentalInfo.windReport?.windGust && (
            <EnvironmentalInfoTooltipItem
              key="windGust"
              label="Wind Gust"
              value={environmentalInfo.windReport.windGust}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related &&
          environmentalInfo.windReport?.windDirection && (
            <EnvironmentalInfoTooltipItem
              key="windDirection"
              label="Wind Direction"
              value={environmentalInfo.windReport.windDirection}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related &&
          environmentalInfo.windReport?.windGustDirection && (
            <EnvironmentalInfoTooltipItem
              key="windGustDirection"
              label="Wind Gust Direction"
              value={environmentalInfo.windReport.windGustDirection}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related &&
          environmentalInfo.windReport?.avgWindSpeed && (
            <EnvironmentalInfoTooltipItem
              key="avgWindSpeed"
              label="Average Wind Speed"
              value={environmentalInfo.windReport.avgWindSpeed}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related &&
          environmentalInfo.weatherReport?.airTemperature && (
            <EnvironmentalInfoTooltipItem
              key="airTemperature"
              label="Air Temperature"
              value={environmentalInfo.weatherReport.airTemperature}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related &&
          environmentalInfo.weatherReport?.airPressure && (
            <EnvironmentalInfoTooltipItem
              key="airPressure"
              label="Air Pressure"
              value={environmentalInfo.weatherReport.airPressure}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related &&
          environmentalInfo.weatherReport?.airPressureTrend && (
            <EnvironmentalInfoTooltipItem
              key="airPressureTrend"
              label="Air Pressure Trend"
              value={environmentalInfo.weatherReport.airPressureTrend}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related &&
          environmentalInfo.weatherReport?.dewPoint && (
            <EnvironmentalInfoTooltipItem
              key="dewPoint"
              label="Dew Point"
              value={environmentalInfo.weatherReport.dewPoint}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          environmentalInfo.seaStateReport?.seaState && (
            <EnvironmentalInfoTooltipItem
              key="seaState"
              label="Sea State"
              value={environmentalInfo.seaStateReport.seaState}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          environmentalInfo.seaStateReport?.swellDirection && (
            <EnvironmentalInfoTooltipItem
              key="swellDirection"
              label="Swell Direction"
              value={environmentalInfo.seaStateReport.swellDirection}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          environmentalInfo.seaStateReport?.swellHeight && (
            <EnvironmentalInfoTooltipItem
              key="swellHeight"
              label="Swell Height"
              value={environmentalInfo.seaStateReport.swellHeight}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          environmentalInfo.seaStateReport?.swellPeriod && (
            <EnvironmentalInfoTooltipItem
              key="swellPeriod"
              label="Swell Period"
              value={environmentalInfo.seaStateReport.swellPeriod}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          environmentalInfo.seaStateReport?.signWaveHeight && (
            <EnvironmentalInfoTooltipItem
              key="signWaveHeight"
              label="Significant Wave Height"
              value={environmentalInfo.seaStateReport.signWaveHeight}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          environmentalInfo.seaStateReport?.waterTemperature && (
            <EnvironmentalInfoTooltipItem
              key="waterTemperature"
              label="Water Temperature"
              value={environmentalInfo.seaStateReport.waterTemperature}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          environmentalInfo.seaStateReport?.waterTemperatureDepth && (
            <EnvironmentalInfoTooltipItem
              key="waterTemperatureDepth"
              label="Water Temperature Depth"
              value={environmentalInfo.seaStateReport.waterTemperatureDepth}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          environmentalInfo.seaStateReport?.waveDirection && (
            <EnvironmentalInfoTooltipItem
              key="waveDirection"
              label="Wave Direction"
              value={environmentalInfo.seaStateReport.waveDirection}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          environmentalInfo.seaStateReport?.wavePeriod && (
            <EnvironmentalInfoTooltipItem
              key="wavePeriod"
              label="Wave Period"
              value={environmentalInfo.seaStateReport.wavePeriod}
            ></EnvironmentalInfoTooltipItem>
          )}

        {ribbonSettings?.show_methyd_station_name &&
          environmentalInfo.stationName && (
            <EnvironmentalInfoTooltipItem
              key="stationName"
              label="Station Name"
              value={environmentalInfo.stationName}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_site_id && environmentalInfo.siteId && (
          <EnvironmentalInfoTooltipItem
            key="siteId"
            label="Site ID"
            value={environmentalInfo.siteId}
          ></EnvironmentalInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_owner && environmentalInfo.owner && (
          <EnvironmentalInfoTooltipItem
            key="owner"
            label="Owner"
            value={environmentalInfo.owner}
          ></EnvironmentalInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_data_timeout &&
          environmentalInfo.dataTimeout && (
            <EnvironmentalInfoTooltipItem
              key="dataTimeout"
              label="Data Timeout"
              value={environmentalInfo.dataTimeout}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_mmsi && environmentalInfo.mmsi && (
          <EnvironmentalInfoTooltipItem
            key="mmsi"
            label="MMSI"
            value={environmentalInfo.mmsi}
          ></EnvironmentalInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_latitude && environmentalInfo.latitude && (
          <EnvironmentalInfoTooltipItem
            key="latitude"
            label="Latitude"
            value={environmentalInfo.latitude.toString()}
          ></EnvironmentalInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_longitude &&
          environmentalInfo.longitude && (
            <EnvironmentalInfoTooltipItem
              key="longitude"
              label="Longitude"
              value={environmentalInfo.longitude.toString()}
            ></EnvironmentalInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_last_origin &&
          environmentalInfo.lastOrigin && (
            <EnvironmentalInfoTooltipItem
              key="lastOrigin"
              label="Last Origin"
              value={environmentalInfo.lastOrigin}
            ></EnvironmentalInfoTooltipItem>
          )}
      </div>
    </CardContent>
  );
};
